<template>
  <div class="clock-page">
    <!-- 页面标题区 -->
    <header class="page-header">
      <div class="header-left">
        <div class="title-indicator"></div>
        <h2 class="page-title">打卡记录</h2>
      </div>
      <div class="header-right">
        <span class="clock-count">本月已累计打卡 {{ monthlyClockDays }} 天</span>
      </div>
    </header>

    <!-- Tab 标签 -->
    <section class="tabs-section">
      <div class="tabs-wrapper">
        <button
          class="tab-button"
          :class="{ active: activeTab === 'video' }"
          @click="activeTab = 'video'"
        >
          视频学习打卡
        </button>
        <button
          class="tab-button"
          :class="{ active: activeTab === 'focus' }"
          @click="activeTab = 'focus'"
        >
          集中学习打卡
        </button>
      </div>
    </section>

    <!-- 打卡记录表格 -->
    <section class="table-section">
      <!-- Loading 加载动画 -->
      <div v-if="loading" class="loading-container">
        <div class="loading-spinner"></div>
        <p class="loading-text">加载中...</p>
      </div>

      <!-- 空数据提示 -->
      <div v-else-if="!loading && clockRecords.length === 0" class="empty-container">
        <i class="el-icon-close empty-icon"></i>
        <p class="empty-text">暂无打卡记录</p>
      </div>

      <!-- 数据表格 -->
      <div v-else class="table-wrapper">
        <table class="clock-table">
          <thead>
            <tr>
              <th>日期</th>
              <th>打卡时间</th>
              <th v-if="activeTab === 'video'">当日学习时长</th>
              <th>观看视频</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(record, index) in clockRecords" :key="index">
              <!-- 日期 -->
              <td class="cell-date">
                <div class="date-cell">
                  <span class="date-value">{{ record.date }}</span>
                  <span class="date-weekday">{{ record.weekday }}</span>
                </div>
              </td>

              <!-- 打卡时间 -->
              <td class="cell-clock-time">
                <span class="clock-time">{{ record.clockTime }}</span>
              </td>

              <!-- 当日学习时长 -->
              <td v-if="activeTab === 'video'" class="cell-duration">
                <div class="duration-cell">
                  <i class="el-icon-date duration-icon"></i>
                  <span class="duration-value">{{ record.duration }} 分钟</span>
                </div>
              </td>

              <!-- 观看视频 -->
              <td class="cell-videos">
                <div ref="videoSummary" class="video-summary">
                  <span
                    v-for="(video, videoIndex) in getVisibleVideos(record, index)"
                    :key="`${index}-${videoIndex}`"
                    class="video-tag"
                    :title="video"
                  >
                    {{ video }}
                  </span>
                  <span
                    v-if="getHiddenVideoCount(record, index) > 0"
                    class="video-tag video-tag-more"
                  >
                    +{{ getHiddenVideoCount(record, index) }}
                  </span>
                  <span v-if="record.videos.length === 0" class="video-empty">-</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- 分页器 -->
    <div class="pagination">
      <div class="pagination-info">
        显示第 {{ (currentPage - 1) * pageSize + 1 }}-{{ Math.min(currentPage * pageSize, totalRecords) }} 条，共 {{ totalRecords }} 条记录
      </div>
      <div class="pagination-controls">
        <button
          class="page-btn"
          :disabled="currentPage === 1"
          @click="previousPage"
        >
          <i class="el-icon-arrow-left"></i>
        </button>

        <button
          v-for="page in visiblePages"
          :key="page"
          class="page-number"
          :class="{ active: page === currentPage }"
          @click="goToPage(page)"
        >
          {{ page }}
        </button>

        <span v-if="totalPages > 3" class="page-ellipsis">...</span>

        <button
          class="page-btn"
          :disabled="currentPage === totalPages"
          @click="nextPage"
        >
          <i class="el-icon-arrow-right"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { getClockStatistics, getClockRecordList, getConcentratedClockRecordList, getUnifiedMonthlyClockDays } from '@/api/api'

const DESKTOP_VIDEO_LAYOUT = {
  tagWidth: 180,
  gap: 8,
  moreWidth: 92,
  defaultCount: 3
}

const MOBILE_VIDEO_LAYOUT = {
  tagWidth: 140,
  gap: 6,
  moreWidth: 84,
  defaultCount: 2
}

const COMPACT_MOBILE_VIDEO_LAYOUT = {
  tagWidth: 120,
  gap: 6,
  moreWidth: 76,
  defaultCount: 2
}

export default {
  name: 'Clock',
  data() {
    return {
      // 当前激活的 Tab
      activeTab: 'video', // 'video' 或 'focus'

      // 本月打卡天数
      monthlyClockDays: 0,

      // 打卡记录数据
      clockRecords: [],

      // 分页相关
      currentPage: 1,
      pageSize: 10,
      totalRecords: 0,

      // 加载状态
      loading: false,

      // 单行视频摘要的可见标题数量
      videoVisibleCounts: {},

      resizeFrameId: null
    }
  },
  computed: {
    // 总页数
    totalPages() {
      return Math.ceil(this.totalRecords / this.pageSize)
    },
    // 可见页码
    visiblePages() {
      const pages = []
      const maxVisible = 3
      for (let i = 1; i <= Math.min(maxVisible, this.totalPages); i++) {
        pages.push(i)
      }
      return pages
    },
    // 当前打卡类型（0=集中学习，1=视频学习）
    currentType() {
      return this.activeTab === 'video' ? '1' : '0'
    }
  },
  watch: {
    // 监听tab切换，重新加载数据
    activeTab() {
      this.currentPage = 1
      this.fetchClockStatistics()
      this.fetchClockRecords()
    },
    // 监听页码变化，重新加载数据
    currentPage() {
      this.fetchClockRecords()
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleWindowResize)

    // 页面加载时获取数据
    this.fetchClockStatistics()
    this.fetchClockRecords()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleWindowResize)

    if (this.resizeFrameId) {
      window.cancelAnimationFrame(this.resizeFrameId)
      this.resizeFrameId = null
    }
  },
  methods: {
    // 获取打卡统计信息
    async fetchClockStatistics() {
      try {
        const res = await getUnifiedMonthlyClockDays()
        if (res.code === 1000) {
          this.monthlyClockDays = res.data
        }
      } catch (error) {
        console.error('获取打卡统计失败:', error)
      }
    },

    // 获取打卡记录列表
    async fetchClockRecords() {
      this.loading = true
      try {
        let res
        if (this.activeTab === 'focus') {
          // 集中学习打卡
          res = await getConcentratedClockRecordList({
            page: this.currentPage,
            pageSize: this.pageSize
          })
        } else {
          // 视频学习打卡
          res = await getClockRecordList({
            type: this.currentType,
            page: this.currentPage,
            pageSize: this.pageSize
          })
        }

        if (res.code === 1000) {
          // 处理记录数据，添加星期几
          this.clockRecords = res.data.records.map(record => ({
            ...record,
            videos: Array.isArray(record.videos) ? record.videos.filter(Boolean) : [],
            weekday: this.getWeekday(record.date)
          }))

          this.totalRecords = res.data.pagination.totalRecords
        }
      } catch (error) {
        console.error('获取打卡记录失败:', error)
        this.clockRecords = []
        this.totalRecords = 0
        this.videoVisibleCounts = {}
      } finally {
        this.loading = false

        if (this.clockRecords.length > 0) {
          this.scheduleVideoCountUpdate()
        }
      }
    },

    scheduleVideoCountUpdate() {
      this.$nextTick(() => {
        this.updateVisibleVideoCounts()
      })
    },

    getVideoLayoutConfig() {
      if (window.innerWidth <= 480) {
        return COMPACT_MOBILE_VIDEO_LAYOUT
      }
      return window.innerWidth <= 768 ? MOBILE_VIDEO_LAYOUT : DESKTOP_VIDEO_LAYOUT
    },

    updateVisibleVideoCounts() {
      const summaryRefs = this.$refs.videoSummary
      const videoSummaries = Array.isArray(summaryRefs)
        ? summaryRefs
        : summaryRefs
          ? [summaryRefs]
          : []
      const nextVisibleCounts = {}

      videoSummaries.forEach((summaryEl, index) => {
        const record = this.clockRecords[index]
        const videoCount = record && Array.isArray(record.videos) ? record.videos.length : 0
        nextVisibleCounts[index] = this.calculateVisibleVideoCount(videoCount, summaryEl.clientWidth)
      })

      this.videoVisibleCounts = nextVisibleCounts
    },

    calculateVisibleVideoCount(videoCount, containerWidth) {
      if (!videoCount) {
        return 0
      }

      const { tagWidth, gap, moreWidth, defaultCount } = this.getVideoLayoutConfig()

      if (!containerWidth) {
        return Math.min(videoCount, defaultCount)
      }

      let visibleCount = Math.floor((containerWidth + gap) / (tagWidth + gap))
      visibleCount = Math.max(1, visibleCount)

      if (visibleCount >= videoCount) {
        return videoCount
      }

      const availableWidth = containerWidth - moreWidth - gap
      visibleCount = Math.floor((availableWidth + gap) / (tagWidth + gap))

      return Math.max(1, Math.min(videoCount - 1, visibleCount))
    },

    getVisibleVideoCount(record, index) {
      const defaultCount = this.getVideoLayoutConfig().defaultCount
      const visibleCount = this.videoVisibleCounts[index]

      if (typeof visibleCount === 'number') {
        return visibleCount
      }

      return Math.min(record.videos.length, defaultCount)
    },

    getVisibleVideos(record, index) {
      return record.videos.slice(0, this.getVisibleVideoCount(record, index))
    },

    getHiddenVideoCount(record, index) {
      return Math.max(0, record.videos.length - this.getVisibleVideoCount(record, index))
    },

    handleWindowResize() {
      if (this.resizeFrameId) {
        window.cancelAnimationFrame(this.resizeFrameId)
      }

      this.resizeFrameId = window.requestAnimationFrame(() => {
        this.updateVisibleVideoCounts()
        this.resizeFrameId = null
      })
    },

    // 根据日期计算星期几
    getWeekday(dateStr) {
      const weekdays = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六']
      const date = new Date(dateStr)
      return weekdays[date.getDay()]
    },

    // 上一页
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--
      }
    },

    // 下一页
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
      }
    },

    // 跳转到指定页
    goToPage(page) {
      this.currentPage = page
    }
  }
}
</script>

<style scoped>
.clock-page {
  width: 100%;
  --video-tag-width: 180px;
  --video-tag-gap: 8px;
  --video-more-tag-min-width: 92px;
}

/* 页面标题区 */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.title-indicator {
  width: 6px;
  height: 24px;
  background: #1a71ff;
  border-radius: 999px;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.clock-count {
  font-size: 14px;
  color: #64748b;
}

/* Tab 标签 */
.tabs-section {
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 24px;
}

.tabs-wrapper {
  display: flex;
  gap: 32px;
}

.tab-button {
  padding: 0 0 12px 0;
  border: none;
  background: transparent;
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  position: relative;
  transition: color 0.2s;
}

.tab-button:hover {
  color: #1a71ff;
}

.tab-button.active {
  color: #1a71ff;
  font-weight: 700;
}

.tab-button.active::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  right: 0;
  height: 2px;
  background: #1a71ff;
  border-radius: 999px;
}

/* 表格区域 */
.table-section {
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  margin-bottom: 24px;
  min-height: 400px;
}

/* Loading 加载动画 */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #f1f5f9;
  border-top-color: #1a71ff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  margin-top: 16px;
  font-size: 14px;
  color: #64748b;
}

/* 空数据提示 */
.empty-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
}

.empty-icon {
  font-size: 64px;
  color: #cbd5e1;
  margin-bottom: 16px;
}

.empty-text {
  font-size: 14px;
  color: #94a3b8;
  margin: 0;
}

.table-wrapper {
  overflow-x: auto;
}

.clock-table {
  width: 100%;
  border-collapse: collapse;
}

.clock-table thead tr {
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.clock-table th {
  padding: 16px 24px;
  text-align: left;
  font-size: 10px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.clock-table tbody tr {
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.2s;
}

.clock-table tbody tr:hover {
  background: rgba(248, 250, 252, 0.5);
}

.clock-table td {
  padding: 20px 24px;
}

/* 日期单元格 */
.date-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.date-value {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}

.date-weekday {
  font-size: 12px;
  color: #94a3b8;
}

/* 打卡时间 */
.clock-time {
  font-size: 14px;
  color: #64748b;
}

/* 学习时长单元格 */
.duration-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.duration-icon {
  color: #1a71ff;
  font-size: 14px;
  font-variation-settings: 'FILL' 1;
}

.duration-value {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}

/* 视频摘要 */
.video-summary {
  display: flex;
  align-items: center;
  gap: var(--video-tag-gap);
  width: 100%;
  min-width: 0;
  overflow: hidden;
}

.video-tag {
  padding: 6px 8px;
  background: rgba(26, 113, 255, 0.05);
  color: #1a71ff;
  font-size: 11px;
  font-weight: 500;
  border-radius: 4px;
  border: 1px solid rgba(26, 113, 255, 0.1);
  width: var(--video-tag-width);
  flex: 0 0 var(--video-tag-width);
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-sizing: border-box;
}

.video-tag-more {
  width: auto;
  min-width: var(--video-more-tag-min-width);
  flex: 0 0 auto;
  color: #475569;
  background: #f8fafc;
  border-style: dashed;
  text-align: center;
}

.video-empty {
  font-size: 13px;
  color: #94a3b8;
}

/* 分页器 */
.pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0;
  border-top: 1px solid #e2e8f0;
}

.pagination-info {
  font-size: 14px;
  color: #64748b;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: white;
  cursor: pointer;
  transition: all 0.2s;
}

.page-btn:disabled {
  color: #cbd5e1;
  cursor: not-allowed;
}

.page-btn:not(:disabled):hover {
  background: #f8fafc;
}


.page-number {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: white;
  font-size: 14px;
  font-weight: 500;
  color: #0f172a;
  cursor: pointer;
  transition: all 0.2s;
}

.page-number:hover {
  background: #f8fafc;
}

.page-number.active {
  background: #1a71ff;
  color: white;
  border-color: #1a71ff;
  box-shadow: 0 1px 3px rgba(26, 113, 255, 0.3);
}

.page-ellipsis {
  color: #cbd5e1;
  padding: 0 4px;
}


/* 响应式 */
@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .page-title {
    font-size: 18px;
  }

  .clock-count {
    font-size: 13px;
  }

  .clock-page {
    --video-tag-width: 140px;
    --video-tag-gap: 6px;
    --video-more-tag-min-width: 84px;
  }

  /* Tab 标签 */
  .tabs-section {
    margin-bottom: 16px;
  }

  .tabs-wrapper {
    width: 100%;
    flex-wrap: wrap;
    gap: 24px;
  }

  .tab-button {
    font-size: 13px;
    padding: 0 0 10px 0;
  }

  /* 表格卡片化 */
  .table-wrapper {
    overflow-x: visible;
    padding: 12px;
  }

  /* 隐藏表头 */
  .clock-table thead {
    display: none;
  }

  .clock-table {
    display: block;
    min-width: auto;
  }

  .clock-table tbody {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  /* 将tr转换为独立卡片 */
  .clock-table tbody tr {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 12px;
    padding: 12px;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    background: white;
    align-items: flex-start;
    width: 100%;
    box-sizing: border-box;
  }

  .clock-table tbody tr:hover {
    background: white;
    border-color: #cbd5e1;
  }

  /* 重置td基础样式 */
  .clock-table tbody td {
    padding: 0;
    border: none;
    display: flex;
    align-items: center;
    min-width: 0;
  }

  /* 第1列：日期 */
  .clock-table tbody td.cell-date {
    grid-column: 1;
    padding: 0;
    margin: 0;
    min-width: 0;
  }

  .date-cell {
    flex-direction: row;
    align-items: baseline;
    gap: 8px;
  }

  .date-value {
    font-size: 14px;
  }

  .date-weekday {
    font-size: 11px;
  }

  /* 第2列：打卡时间 */
  .clock-table tbody td.cell-clock-time {
    grid-column: 2;
    padding: 0;
    margin: 0;
    justify-content: flex-end;
    text-align: right;
  }

  .clock-time {
    font-size: 13px;
  }

  /* 第3列：学习时长（视频打卡有）*/
  .clock-table tbody td.cell-duration {
    grid-column: 1 / -1;
    padding: 0;
    margin: 0;
  }

  .duration-cell {
    font-size: 13px;
    gap: 6px;
  }

  .duration-icon {
    font-size: 12px;
  }

  .duration-value {
    font-size: 13px;
  }

  /* 观看视频标签 */
  .clock-table tbody td.cell-videos {
    grid-column: 1 / -1;
    padding: 0;
    margin: 0;
  }

  .video-summary {
    width: 100%;
  }

  .video-tag {
    padding: 4px 6px;
    font-size: 10px;
  }

  /* 分页器优化 */
  .pagination {
    flex-direction: column;
    gap: 12px;
    padding: 12px 0;
    border-top: 1px solid #e2e8f0;
  }

  .pagination-info {
    font-size: 13px;
    text-align: center;
    line-height: 1.5;
  }

  .pagination-controls {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
  }

  /* 隐藏中间的页码按钮和省略号 */
  .page-number {
    display: none;
  }

  .page-ellipsis {
    display: none;
  }

  /* 放大箭头按钮 */
  .page-btn {
    width: 40px;
    height: 40px;
    font-size: 16px;
  }

  .page-btn:not(:disabled):hover {
    background: #f1f5f9;
  }

  /* 表格区域 */
  .table-section {
    margin-bottom: 16px;
  }

  /* 空数据和Loading样式 */
  .loading-container,
  .empty-container {
    padding: 60px 20px;
  }

  .empty-icon {
    font-size: 48px;
  }

  .empty-text,
  .loading-text {
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .clock-page {
    --video-tag-width: 120px;
    --video-tag-gap: 6px;
    --video-more-tag-min-width: 76px;
  }

  .page-header {
    gap: 10px;
    margin-bottom: 18px;
  }

  .header-left {
    gap: 10px;
  }

  .header-right {
    width: 100%;
  }

  .clock-count {
    display: block;
    line-height: 1.5;
  }

  .tabs-wrapper {
    gap: 10px;
  }

  .tab-button {
    flex: 1 1 calc(50% - 5px);
    min-width: 0;
    padding-bottom: 10px;
    text-align: left;
  }

  .table-wrapper {
    padding: 10px;
  }

  .clock-table tbody tr {
    grid-template-columns: minmax(0, 1fr);
    gap: 8px;
    padding: 10px;
  }

  .clock-table tbody td.cell-date,
  .clock-table tbody td.cell-clock-time,
  .clock-table tbody td.cell-duration,
  .clock-table tbody td.cell-videos {
    grid-column: 1 / -1;
  }

  .clock-table tbody td.cell-clock-time {
    justify-content: flex-start;
    text-align: left;
  }

  .date-cell {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }

  .date-value,
  .clock-time,
  .duration-value {
    font-size: 13px;
  }

  .date-weekday {
    font-size: 11px;
  }

  .video-tag {
    width: min(100%, var(--video-tag-width));
    flex-basis: min(100%, var(--video-tag-width));
  }

  .pagination {
    gap: 10px;
    align-items: stretch;
  }

  .pagination-controls {
    gap: 10px;
  }

  .pagination-info {
    font-size: 12px;
  }
}
</style>
